/**
 * Header notification bell + unread badge + dropdown feed (issue #1436, EPIC #608).
 *
 * Mounted in BOTH shells (agent CRM + admin) next to the search button. Polls
 * the self-scoped unread count for the badge, opens a dropdown/feed of the
 * user's own notifications, supports mark-read / mark-all-read, and navigates to
 * a notification's link on click.
 *
 * Layout (#2029): the feed is rendered in a portal to document.body so it
 * escapes the sticky, backdrop-blurred shell header — a `backdrop-filter`
 * ancestor establishes a containing block for `position: fixed` descendants,
 * which previously trapped the bottom-sheet as a cramped little box pinned to
 * the top of the header (the reported bug). Out of the header:
 * - Mobile: a comfortable full-width bottom-sheet with its own header (title +
 *   mark-all-read + close), a generous height, an internally-scrolling LIST
 *   (the sheet does not shrink to content), >=44px targets and safe-area aware.
 * - Desktop: a clean anchored panel beneath the bell, internally scrolling.
 * - Dark mode + brand tokens throughout; agency-neutral, emoji-free copy.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import {
  type AppNotification,
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../api/notifications";
import { useDecisionToasts } from "./useDecisionToasts";
import { useIsViewingAs } from "../../contexts/viewAs";

const POLL_INTERVAL_MS = 30_000;

/** Gap between the bell and the desktop panel (px). */
const PANEL_GAP = 8;
/** Keep the desktop panel off the viewport edge (px). */
const VIEWPORT_MARGIN = 8;

/** Whether the desktop breakpoint (>=640px) is active, guarding environments
 *  (jsdom) where matchMedia is unavailable. */
function desktopMatches(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(min-width: 640px)").matches;
}

/** Compact "x ago" label without pulling in a date library. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationBell() {
  const navigate = useNavigate();
  // D2-SEC-011 (#3962): the feed reads as the agent's, but nothing is marked read.
  const viewingAs = useIsViewingAs();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Desktop anchor coordinates, computed from the bell's bounding rect so the
  // body-portalled panel still lines up under the button.
  const [anchor, setAnchor] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  // Only anchor with inline coordinates on desktop (>=640px). On mobile the
  // sheet is a Tailwind-driven full-width bottom-sheet, so inline top/right
  // would fight the `inset-x-0 bottom-0` classes (inline styles win over
  // classes), pinning the sheet to a corner instead.
  const [isDesktop, setIsDesktop] = useState(desktopMatches);

  // #3801: pop up new sign-off decision / ready-to-process notifications
  // (riding this bell's unread poll); opening one drops the badge by one.
  useDecisionToasts(
    unread,
    useCallback(() => setUnread((c) => Math.max(0, c - 1)), []),
  );

  const refreshCount = useCallback(async (signal?: AbortSignal) => {
    try {
      const count = await getUnreadCount(signal);
      setUnread(count);
    } catch {
      // Silent: a transient failure should not surface an error in the header.
    }
  }, []);

  // Track the desktop breakpoint so the inline anchor only applies there.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Poll the unread count for the badge.
  useEffect(() => {
    const controller = new AbortController();
    refreshCount(controller.signal);
    const id = window.setInterval(() => refreshCount(), POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      window.clearInterval(id);
    };
  }, [refreshCount]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNotifications();
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the feed when the panel opens.
  useEffect(() => {
    if (open) {
      void loadFeed();
    }
  }, [open, loadFeed]);

  // Position the desktop panel under the bell whenever it opens, and keep it
  // aligned on resize/scroll. The right edge is anchored to the bell's right
  // edge (clamped to the viewport) so the panel hangs down-and-left like the
  // previous `sm:right-0` anchored dropdown.
  useLayoutEffect(() => {
    if (!open) return;
    function reposition() {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const right = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right);
      setAnchor({ top: rect.bottom + PANEL_GAP, right });
    }
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function handleMarkRead(n: AppNotification) {
    // Marking read is a write the server refuses while viewing as an agent.
    if (!n.is_read && !viewingAs) {
      try {
        await markNotificationRead(n.id);
        setItems((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)),
        );
        setUnread((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    }
  }

  async function handleMarkAll() {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch {
      // ignore
    }
  }

  async function handleOpenItem(n: AppNotification) {
    await handleMarkRead(n);
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    }
  }

  const feed = (
    <>
      {/* Loading / empty / list — this region scrolls, the sheet does not. */}
      {loading ? (
        <p className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
          Loading...
        </p>
      ) : items.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-[var(--color-text-muted)]">
          You have no notifications.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {items.map((n) => (
            <li key={n.id}>
              <div
                className={`flex items-start gap-3 px-4 py-3 ${
                  n.is_read ? "" : "bg-[var(--color-bg-page)]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleOpenItem(n)}
                  className="flex-1 text-left min-h-[44px]"
                >
                  <p
                    className={`text-sm ${
                      n.is_read
                        ? "text-[var(--color-text-secondary)]"
                        : "text-[var(--color-text-primary)] font-medium"
                    }`}
                  >
                    {n.action_required && (
                      <span className="mr-1.5 inline-flex items-center rounded-full bg-[var(--color-accent-subtle)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-accent)] align-middle">
                        Action needed
                      </span>
                    )}
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="mt-0.5 text-xs text-[var(--color-text-muted)] line-clamp-2">
                      {n.body}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                    {timeAgo(n.created_at)}
                  </p>
                </button>
                {!n.is_read && !viewingAs && (
                  <button
                    type="button"
                    onClick={() => handleMarkRead(n)}
                    aria-label="Mark read"
                    title="Mark read"
                    className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--color-bg-card)] text-[var(--color-text-muted)]"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        data-tour="header-notifications"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        className="relative min-h-[44px] min-w-[44px] p-2.5 rounded-lg hover:bg-[var(--color-bg-page)] transition-colors flex items-center justify-center"
      >
        <Bell className="h-5 w-5 text-[var(--color-text-secondary)]" />
        {unread > 0 && (
          <span
            aria-label={`${unread} unread notifications`}
            className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-danger)] text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop. Full-screen dim on mobile; invisible click-catcher on
                desktop so an outside click still closes the anchored panel. */}
            <div
              className="fixed inset-0 z-[70] bg-black/40 sm:bg-transparent"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Notifications"
              style={
                isDesktop
                  ? { top: anchor.top, right: anchor.right }
                  : undefined
              }
              className="
                fixed inset-x-0 bottom-0 z-[80]
                flex flex-col overflow-hidden
                h-[70vh] max-h-[70vh] rounded-t-2xl
                pb-[env(safe-area-inset-bottom)]
                sm:inset-x-auto sm:bottom-auto
                sm:h-auto sm:w-96 sm:max-h-[32rem] sm:rounded-xl sm:pb-0
                bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-2xl
              "
            >
              {/* Sheet header (sticky within the sheet). */}
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--color-border)] shrink-0">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  Notifications
                </h2>
                <div className="flex items-center gap-1">
                  {!viewingAs && (
                    <button
                      type="button"
                      onClick={handleMarkAll}
                      disabled={unread === 0}
                      className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] px-2 rounded-lg"
                    >
                      <CheckCheck className="h-4 w-4" />
                      Mark all read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close notifications"
                    title="Close"
                    className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--color-bg-page)] text-[var(--color-text-muted)]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* The LIST scrolls; flex-1 + min-h-0 keeps the sheet at its full
                  height instead of collapsing to content (#2029). */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                {feed}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
